/**
 * IncomeSources + ExpenseCategories + Assumptions — bundled for a smaller footprint.
 */
import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { NumberField, TextField, SelectField, Money, StatCell } from "./primitives";
import { useFinancialPlan } from "@/context/FinancialPlanContext";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useEffect } from "react";

const INCOME_KINDS = [
  ["salary", "Salary"], ["bonus", "Bonus"], ["commission", "Commission"],
  ["side_hustle", "Side hustle"], ["rental", "Rental"], ["investment", "Investment"],
  ["royalty", "Royalty"], ["passive", "Passive"], ["other", "Other"],
];

const EXPENSE_CATS = [
  ["housing", "Housing"], ["transportation", "Transportation"],
  ["insurance", "Insurance"], ["utilities", "Utilities"], ["food", "Food"],
  ["entertainment", "Entertainment"], ["subscriptions", "Subscriptions"],
  ["healthcare", "Healthcare"], ["childcare", "Childcare"], ["travel", "Travel"],
  ["pets", "Pets"], ["education", "Education"], ["debt", "Debt payments"],
  ["misc", "Miscellaneous"], ["custom", "Custom"],
];


// ==================== IncomeSources ====================
export function IncomeSources() {
  const { plan, summary, patch } = useFinancialPlan();
  const items = plan?.income_sources || [];
  const [busy, setBusy] = useState(false);
  const upsert = async (upd) => { setBusy(true); try { await patch({ income_sources: upd }); } finally { setBusy(false); } };
  const add = () => upsert([...items, { id: `inc_${Date.now()}`, name: "New income", kind: "salary", amount_annual: 0, taxable: true, growth_pct: 0 }]);
  const remove = (id) => upsert(items.filter((i) => i.id !== id));
  const patchOne = (id, changes) => upsert(items.map((i) => i.id === id ? { ...i, ...changes } : i));
  return (
    <div className="space-y-3" data-testid="income-panel">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <StatCell label="Annual income" value={<Money amount={summary?.total_income_annual} currency={plan?.currency} />} testId="income-annual" />
        <StatCell label="Monthly gross" value={<Money amount={summary?.total_income_monthly} currency={plan?.currency} />} testId="income-monthly" />
        <StatCell label="Sources" value={items.length} testId="income-count" />
      </div>
      {items.map((i) => (
        <div key={i.id} className="border border-border rounded-lg p-3 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid={`income-row-${i.id}`}>
          <TextField label="Name" value={i.name} onChange={(v) => patchOne(i.id, { name: v })} testId={`income-name-${i.id}`} />
          <SelectField label="Kind" value={i.kind} onChange={(v) => patchOne(i.id, { kind: v })} options={INCOME_KINDS} testId={`income-kind-${i.id}`} />
          <NumberField label="Annual amount" value={i.amount_annual} onChange={(v) => patchOne(i.id, { amount_annual: v })} prefix={plan?.currency} testId={`income-amount-${i.id}`} />
          <div className="flex items-end justify-end">
            <button onClick={() => remove(i.id)} className="text-xs text-destructive hover:underline flex items-center gap-1" data-testid={`income-remove-${i.id}`}><Trash2 size={12} /> Remove</button>
          </div>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={add} disabled={busy} data-testid="income-add-btn"><Plus size={13} className="mr-1" /> Add income source</Button>
    </div>
  );
}


// ==================== ExpenseCategories ====================
export function ExpenseCategories() {
  const { plan, summary, patch } = useFinancialPlan();
  const items = plan?.expenses || [];
  const [busy, setBusy] = useState(false);
  const upsert = async (upd) => { setBusy(true); try { await patch({ expenses: upd }); } finally { setBusy(false); } };
  const add = () => upsert([...items, { category: "misc", amount_monthly: 0 }]);
  const remove = (idx) => upsert(items.filter((_, i) => i !== idx));
  const patchOne = (idx, changes) => upsert(items.map((e, i) => i === idx ? { ...e, ...changes } : e));
  return (
    <div className="space-y-3" data-testid="expenses-panel">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <StatCell label="Monthly expenses" value={<Money amount={summary?.total_expenses_monthly} currency={plan?.currency} />} testId="exp-monthly" />
        <StatCell label="Annual expenses" value={<Money amount={summary?.total_expenses_annual} currency={plan?.currency} />} testId="exp-annual" />
        <StatCell label="Categories" value={items.length} testId="exp-count" />
      </div>
      {items.map((e, idx) => (
        <div key={idx} className="border border-border rounded-lg p-3 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid={`exp-row-${idx}`}>
          <SelectField label="Category" value={e.category} onChange={(v) => patchOne(idx, { category: v })} options={EXPENSE_CATS} testId={`exp-cat-${idx}`} />
          <NumberField label="Monthly amount" value={e.amount_monthly} onChange={(v) => patchOne(idx, { amount_monthly: v })} prefix={plan?.currency} testId={`exp-amount-${idx}`} />
          <div className="flex items-end justify-end">
            <button onClick={() => remove(idx)} className="text-xs text-destructive hover:underline flex items-center gap-1" data-testid={`exp-remove-${idx}`}><Trash2 size={12} /> Remove</button>
          </div>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={add} disabled={busy} data-testid="exp-add-btn"><Plus size={13} className="mr-1" /> Add expense category</Button>
    </div>
  );
}


// ==================== Assumptions (inflation, return, tax) ====================
export function Assumptions() {
  const { plan, summary, patch } = useFinancialPlan();
  const a = plan?.assumptions || {};
  const [form, setForm] = useState({
    inflation_pct: a.inflation_pct ?? 2,
    default_return_pct: a.default_return_pct ?? 7,
    salary_growth_pct: a.salary_growth_pct ?? 3,
    tax_country: a.tax_country || "",
    tax_region: a.tax_region || "",
    filing_status: a.filing_status || "single",
  });
  const [countries, setCountries] = useState([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { api.get("/financial-plan/tax/countries").then(({ data }) => setCountries(data.countries || [])).catch(() => {}); }, []);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };
  const save = async () => { await patch({ assumptions: form }); setDirty(false); };

  const regions = countries.find((c) => c.code === form.tax_country)?.regions || [];

  return (
    <div className="space-y-4" data-testid="assumptions-panel">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <NumberField label="Inflation" value={form.inflation_pct} onChange={(v) => set("inflation_pct", v)} suffix="%" testId="assump-inflation" />
        <NumberField label="Expected annual return" value={form.default_return_pct} onChange={(v) => set("default_return_pct", v)} suffix="%" testId="assump-return" />
        <NumberField label="Salary growth" value={form.salary_growth_pct} onChange={(v) => set("salary_growth_pct", v)} suffix="%" testId="assump-salary-growth" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SelectField label="Tax country" value={form.tax_country} onChange={(v) => set("tax_country", v)} options={[["", "— none —"], ...countries.map((c) => [c.code, c.code])]} testId="assump-country" />
        <SelectField label="Region / state" value={form.tax_region} onChange={(v) => set("tax_region", v)} options={[["", "— none —"], ...regions.map((r) => [r, r])]} testId="assump-region" />
        <SelectField label="Filing status" value={form.filing_status} onChange={(v) => set("filing_status", v)} options={[["single", "Single"], ["married", "Married"]]} testId="assump-filing" />
      </div>
      {summary?.tax && (
        <div className="border border-border rounded-lg p-3 bg-muted/20 text-xs">
          <div className="font-mono uppercase tracking-widest text-muted-foreground mb-1">Estimated taxes ({summary.tax.country}/{summary.tax.region || "federal only"})</div>
          <div className="grid grid-cols-3 gap-2">
            <div>Marginal: <b>{summary.tax.marginal_rate_pct}%</b></div>
            <div>Average: <b>{summary.tax.average_rate_pct}%</b></div>
            <div>Annual tax: <b><Money amount={summary.tax.estimated_annual_tax} currency={plan?.currency} /></b></div>
          </div>
        </div>
      )}
      <div className="flex justify-end">
        <Button size="sm" onClick={save} disabled={!dirty} data-testid="assump-save-btn">Save assumptions</Button>
      </div>
    </div>
  );
}
